import { Spinner, Text, mergeClasses } from "@fluentui/react-components";
import { useEffect, useMemo, useState } from "react";
import { getSharedClientDocuments } from "apps/project-iq/api";
import { getFileTypeInfo } from "apps/project-iq/utils/fileUtils";
import { DocumentIcon } from "icons/DocumentIcon";
import { useStyles } from "./ClientDeliverablesPage.styles";
import {
  IClientDeliverablesPageProps,
  ISharedClientDocument,
  ISharedClientDocumentDisplay,
} from "./ClientDeliverablesPage.types";

type FileIconStyleKey = "pdf" | "xls" | "ppt" | "default";

export const ClientDeliverablesPage = ({
  workspaceId,
}: IClientDeliverablesPageProps): JSX.Element => {
  const styles = useStyles();
  const [documents, setDocuments] = useState<ISharedClientDocument[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    let isCancelled = false;

    const fetchSharedDocuments = async () => {
      setIsLoading(true);
      setErrorMessage("");
      try {
        const sharedDocuments = await getSharedClientDocuments({
          workspaceId,
        });
        if (!isCancelled) {
          setDocuments(sharedDocuments || []);
        }
      } catch {
        if (!isCancelled) {
          setErrorMessage("Failed to load deliverables.");
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchSharedDocuments();

    return () => {
      isCancelled = true;
    };
  }, [workspaceId]);

  const iconStyleMap: Record<FileIconStyleKey, string> = useMemo(
    () => ({
      pdf: styles.fileIconPdf,
      xls: styles.fileIconXls,
      ppt: styles.fileIconPpt,
      default: styles.fileIconDefault,
    }),
    [styles],
  );

  const displayDocuments: ISharedClientDocumentDisplay[] = useMemo(
    () =>
      documents.map((doc) => {
        const typeInfo = getFileTypeInfo(doc.originalFilename);
        return {
          filename: doc.filename,
          originalFilename: doc.originalFilename,
          fileTypeLabel: typeInfo.label,
          fileTypeStyle: typeInfo.style,
          sharedDateLabel: doc.sharedAt
            ? new Date(doc.sharedAt * 1000).toLocaleString()
            : "",
        };
      }),
    [documents],
  );

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        {errorMessage && <div className={styles.errorBox}>{errorMessage}</div>}

        {isLoading ? (
          <div className={styles.emptyState}>
            <Spinner size="small" aria-label="Loading deliverables" />
          </div>
        ) : displayDocuments.length === 0 ? (
          <div className={styles.emptyState}>
            <Text>No deliverables have been shared yet.</Text>
          </div>
        ) : (
          <div className={styles.fileListGroup}>
            <div className={styles.columnHeaderRow}>
              <Text className={styles.fileNameColumnHeader}>File Name</Text>
              <Text className={styles.sharedDateColumnHeader}>Shared At</Text>
            </div>
            <div
              className={styles.fileListCard}
              role="list"
              aria-label="Shared deliverables"
            >
              {displayDocuments.map((doc) => {
                const iconClass =
                  iconStyleMap[doc.fileTypeStyle as FileIconStyleKey] ??
                  styles.fileIconDefault;

                return (
                  <div
                    key={doc.filename}
                    role="listitem"
                    className={styles.fileRow}
                  >
                    <div
                      className={mergeClasses(
                        styles.fileIconWrapper,
                        iconClass,
                      )}
                    >
                      <DocumentIcon size={20} aria-hidden="true" />
                    </div>
                    <div className={styles.fileInfo}>
                      <Text className={styles.fileName}>
                        {doc.originalFilename}
                      </Text>
                      <Text className={styles.fileMeta}>
                        {doc.fileTypeLabel}
                      </Text>
                    </div>
                    {doc.sharedDateLabel && (
                      <Text className={styles.sharedDate}>
                        {doc.sharedDateLabel}
                      </Text>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
